import React, { useState } from 'react';
import { X, Ship, MapPin, Navigation, Anchor, Clock, ArrowRight, Play, Pause, RotateCcw } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';
import { pastTracks, getStatusColor } from '@/data/mockData';

const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-muted-foreground flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </span>
    <span className="text-sm font-medium text-foreground">{value || '-'}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const colorClass = getStatusColor(status);
  return (
    <Badge 
      variant="outline" 
      className={cn(
        "text-xs",
        colorClass === 'vessel-active' && "border-success text-success",
        colorClass === 'vessel-anchored' && "border-primary text-primary",
        colorClass === 'info' && "border-info text-info"
      )}
    >
      {status}
    </Badge>
  );
};

export const VesselDrawer = () => {
  const { selectedVessel, vesselDrawerOpen, closeVesselDrawer } = useApp();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState([0]);

  if (!selectedVessel) return null;

  const vesselTracks = pastTracks[selectedVessel.id] || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Sheet open={vesselDrawerOpen} onOpenChange={(open) => !open && closeVesselDrawer()}>
      <SheetContent className="w-[420px] sm:max-w-[420px] p-0 overflow-hidden">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b border-border bg-accent/30">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Ship className="w-6 h-6 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-lg font-semibold">{selectedVessel.name}</SheetTitle>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">🇮🇩</span>
                  <span className="text-xs text-muted-foreground">{selectedVessel.callsign}</span>
                  <Separator orientation="vertical" className="h-3" />
                  <StatusBadge status={selectedVessel.status} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="text-center p-2 bg-card rounded-lg">
              <p className="text-xs text-muted-foreground">MMSI</p>
              <p className="text-sm font-medium">{selectedVessel.mmsi}</p>
            </div>
            <div className="text-center p-2 bg-card rounded-lg">
              <p className="text-xs text-muted-foreground">IMO</p>
              <p className="text-sm font-medium">{selectedVessel.imo}</p>
            </div>
            <div className="text-center p-2 bg-card rounded-lg">
              <p className="text-xs text-muted-foreground">Callsign</p>
              <p className="text-sm font-medium">{selectedVessel.callsign}</p>
            </div>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="flex-1">
          <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent px-6 h-12">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="track" className="text-xs">Past Track</TabsTrigger>
            <TabsTrigger value="forecast" className="text-xs">Forecast</TabsTrigger>
            <TabsTrigger value="playback" className="text-xs">Playback</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[calc(100vh-280px)]">
            {/* Overview Tab */}
            <TabsContent value="overview" className="m-0 p-6 space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-foreground">Vessel Information</h3>
                <div className="bg-muted/50 rounded-lg p-4 space-y-1">
                  <InfoRow icon={Ship} label="Ship Type" value={selectedVessel.shipType} />
                  <Separator />
                  <InfoRow label="Length / Beam" value={`${selectedVessel.length}m / ${selectedVessel.beam}m`} />
                  <Separator />
                  <InfoRow label="Draught" value={`${selectedVessel.draught}m`} />
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-sm font-medium text-foreground">Navigation Data</h3>
                <div className="bg-muted/50 rounded-lg p-4 space-y-1">
                  <InfoRow icon={Navigation} label="Speed (SOG)" value={`${selectedVessel.sog} kn`} />
                  <Separator />
                  <InfoRow label="Course (COG)" value={`${selectedVessel.cog}°`} />
                  <Separator />
                  <InfoRow label="Heading" value={`${selectedVessel.heading}°`} />
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-sm font-medium text-foreground">Voyage Details</h3>
                <div className="bg-muted/50 rounded-lg p-4 space-y-1">
                  <InfoRow icon={MapPin} label="Destination" value={selectedVessel.destination} />
                  <Separator />
                  <InfoRow icon={Clock} label="ETA" value={formatDate(selectedVessel.eta)} />
                  <Separator />
                  <InfoRow icon={Anchor} label="Status" value={selectedVessel.status} />
                </div>
              </div>

              <div className="space-y-1">
                <h3 className="text-sm font-medium text-foreground">Position</h3>
                <div className="bg-muted/50 rounded-lg p-4 space-y-1">
                  <InfoRow label="Latitude" value={`${selectedVessel.lat.toFixed(6)}°`} />
                  <Separator />
                  <InfoRow label="Longitude" value={`${selectedVessel.lon.toFixed(6)}°`} />
                </div>
              </div>
            </TabsContent>

            {/* Past Track Tab */}
            <TabsContent value="track" className="m-0 p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-foreground">Track History</h3>
                  <Badge variant="outline">{vesselTracks.length} points</Badge>
                </div>
                
                {vesselTracks.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Time</TableHead>
                        <TableHead className="text-xs">Lat</TableHead>
                        <TableHead className="text-xs">Lon</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vesselTracks.map((track, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs">{formatDate(track.timestamp)}</TableCell>
                          <TableCell className="text-xs font-mono">{track.lat.toFixed(4)}</TableCell>
                          <TableCell className="text-xs font-mono">{track.lon.toFixed(4)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Navigation className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No track history available</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Route Forecast Tab */}
            <TabsContent value="forecast" className="m-0 p-6">
              <div className="space-y-4">
                <div className="bg-accent/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <h3 className="text-sm font-medium text-foreground">Route Forecast</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Computed route prediction based on current heading, speed, and destination port.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Estimated Arrival</p>
                      <p className="text-sm font-medium">{formatDate(selectedVessel.eta)}</p>
                    </div>
                    <Badge>On Schedule</Badge>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Remaining Distance</p>
                      <p className="text-sm font-medium">~45 NM</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>

                  <div className="p-4 border border-dashed border-border rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">
                      Route forecast visualization will be displayed on the map when this feature is enabled.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Playback Tab */}
            <TabsContent value="playback" className="m-0 p-6">
              <div className="space-y-6">
                <div className="bg-accent/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-foreground mb-2">Track Playback</h3>
                  <p className="text-xs text-muted-foreground">
                    Replay vessel movement history on the map.
                  </p>
                </div>

                {/* Playback Controls */}
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setPlaybackPosition([0])}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon"
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="w-12 h-12"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5 ml-0.5" />
                      )}
                    </Button>
                  </div>

                  {/* Time Slider */}
                  <div className="space-y-2">
                    <Slider
                      value={playbackPosition}
                      onValueChange={setPlaybackPosition}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>00:00</span>
                      <span>Current: {playbackPosition[0]}%</span>
                      <span>04:00</span>
                    </div>
                  </div>

                  {/* Speed Control */}
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-xs text-muted-foreground">Speed:</span>
                    <Button variant="outline" size="sm" className="text-xs">1x</Button>
                    <Button variant="outline" size="sm" className="text-xs">2x</Button>
                    <Button variant="outline" size="sm" className="text-xs">4x</Button>
                  </div>
                </div>

                <div className="p-4 border border-dashed border-border rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">
                    Playback will animate the vessel position on the map based on historical track data.
                  </p>
                </div>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default VesselDrawer;
